import React from 'react'

import ProfileSection from 'app/common/pixel-menu/_comp/ProfileSection'
import {
  DcBreadcrumbs,
  DcButton,
  DcDropDown,
  DcFlexBox,
  DcOverlaySlider,
  DcText,
  colors,
  PixelPageContainer,
  PixelTable,
  Section,
  SectionContent,
  PixelDate,
  PixelSpinner,
} from 'pixel-base'
import type { AjaxResponse } from 'rxjs/ajax'
import {
  getSalesHistory,
  getSalesHistoryDetail,
  SalesHistoryItem,
  type SalesHistoryDetailItem,
  type SalesHistoryDetailResponse,
} from 'utils/api-routes-planning'
import { ArrowTrendUpIcon } from 'utils/icons/arrow-trend-up-icon'
import { CalendarDaysIcon } from 'utils/icons/calendar-days-icon'
import { GrowthFactorIcon } from 'utils/icons/growth-factor-icon'
import { TotalProductsIcon } from 'utils/icons/total-products-icon'
import { UploadIcon } from 'utils/icons/upload-icon'
import BulkSalesUpload from './components/BulkSalesUpload'
import SalesTable from './components/SalesTable'

const breadcrumbs = [
  {
    name: 'Production Planning',
    route: '/planning',
  },
  {
    name: 'Sales',
    active: true,
  },
]

const Sales: React.FC = () => {
  const [showUploadSlider, setShowUploadSlider] = React.useState(false)
  const [salesRows, setSalesRows] = React.useState<SalesHistoryItem[]>([])
  const [showDetailSlider, setShowDetailSlider] = React.useState(false)
  const [detailLoading, setDetailLoading] = React.useState(false)
  const [saleDetail, setSaleDetail] = React.useState<
    SalesHistoryDetailItem[] | null
  >(null)
  const [selectedSale, setSelectedSale] =
    React.useState<SalesHistoryItem | null>(null)
  const [isLoading, setIsLoading] = React.useState(false)
  const [selectedMonth, setSelectedMonth] = React.useState<string>('all')
  const [selectedYear, setSelectedYear] = React.useState<string>('all')
  const [selectedChannel, setSelectedChannel] = React.useState<string>('all')

  const monthNameToNumber = (value: string): number | undefined => {
    switch (value.toLowerCase()) {
      case 'january':
        return 1
      case 'february':
        return 2
      case 'march':
        return 3
      case 'april':
        return 4
      case 'may':
        return 5
      case 'june':
        return 6
      case 'july':
        return 7
      case 'august':
        return 8
      case 'september':
        return 9
      case 'october':
        return 10
      case 'november':
        return 11
      case 'december':
        return 12
      default:
        return undefined
    }
  }

  const fetchSalesHistory = React.useCallback(() => {
    const filters: Record<string, string | number | boolean | undefined> = {}

    if (selectedMonth !== 'all') {
      const monthNumber = monthNameToNumber(selectedMonth)
      if (monthNumber) {
        filters.sale_month = monthNumber
      }
    }

    if (selectedYear !== 'all') {
      const yearNumber = Number(selectedYear)
      if (!Number.isNaN(yearNumber)) {
        filters.sale_year = yearNumber
      }
    }

    if (selectedChannel !== 'all') {
      filters.sale_channel = selectedChannel
    }

    setIsLoading(true)
    const subscription = getSalesHistory(filters).subscribe({
      next: (res) => {
        setSalesRows(res.response?.data || [])
        setIsLoading(false)
      },
      error: () => {
        setIsLoading(false)
      },
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [selectedMonth, selectedYear, selectedChannel])

  React.useEffect(() => {
    const cleanup = fetchSalesHistory()
    return cleanup
  }, [fetchSalesHistory])

  const handleRowClick = (row: SalesHistoryItem) => {
    if (!row || typeof row.id !== 'number') {
      return
    }

    setSelectedSale(row)
    setShowDetailSlider(true)
    setDetailLoading(true)
    setSaleDetail(null)

    const subscription = getSalesHistoryDetail(row.id).subscribe({
      next: (res: AjaxResponse<SalesHistoryDetailResponse>) => {
        setSaleDetail(res.response?.data ?? null)
        setDetailLoading(false)
      },
      error: () => {
        setSaleDetail(null)
        setDetailLoading(false)
      },
    })

    return () => {
      subscription.unsubscribe()
    }
  }

  const detailColumns = [
    {
      dataKey: 'product_sku',
      name: 'Product SKU',
      sortable: true,
      minWidth: '150px',
      selector: (row: SalesHistoryDetailItem) => row.product_sku || '--',
    },
    {
      dataKey: 'product_name',
      name: 'Product Name',
      sortable: true,
      minWidth: '200px',
      selector: (row: SalesHistoryDetailItem) => row.product_name || '--',
    },
    {
      dataKey: 'qty_sold',
      name: 'Qty Sold',
      width: '120px',
      sortable: true,
      selector: (row: SalesHistoryDetailItem) =>
        typeof row.qty_sold === 'number' ? row.qty_sold : '--',
    },
  ]

  return (
    <>
      <DcFlexBox
        height="60px"
        style={{
          fontSize: '16px',
          padding: '8px 0px',
          borderBottom: '1px solid #e5e5e5',
        }}
      >
        <DcFlexBox
          justifyContent="space-between"
          alignItems="center"
          padding=".5rem 20px"
          width="100%"
        >
          <DcBreadcrumbs
            className="breadcrumbs"
            fontSize="16px"
            data={breadcrumbs}
          />
          <ProfileSection />
        </DcFlexBox>
      </DcFlexBox>

      <PixelPageContainer padding="0px" height="calc(100vh - 61px)">
        <Section style={{ padding: '0px' }}>
          <SectionContent style={{ padding: '5px 20px' }}>
            <DcFlexBox
              height="auto"
              width="100%"
              justifyContent="space-between"
              flexDirection="row-reverse"
            >
              <DcFlexBox height="auto" width="auto" gap="20px">
                <DcButton
                  variant="primary"
                  size="sm"
                  onClick={() => setShowUploadSlider(true)}
                  withIcon={true}
                >
                  <UploadIcon color="white" size={16} />
                  <span>Upload Sales</span>
                </DcButton>
              </DcFlexBox>
              <DcFlexBox width="70%" gap="5px" height="auto">
                <DcDropDown
                  dropdownSize="sm"
                  options={[
                    { label: 'All Month', value: 'all' },
                    { label: 'January', value: 'january' },
                    { label: 'February', value: 'february' },
                    { label: 'March', value: 'march' },
                    { label: 'April', value: 'april' },
                    { label: 'May', value: 'may' },
                    { label: 'June', value: 'june' },
                    { label: 'July', value: 'july' },
                    { label: 'August', value: 'august' },
                    { label: 'September', value: 'september' },
                    { label: 'October', value: 'october' },
                    { label: 'November', value: 'november' },
                    { label: 'December', value: 'december' },
                  ]}
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                />
                <DcDropDown
                  dropdownSize="sm"
                  options={[
                    { label: 'All Year', value: 'all' },
                    { label: '2025', value: '2025' },
                    { label: '2024', value: '2024' },
                    { label: '2023', value: '2023' },
                    { label: '2022', value: '2022' },
                    { label: '2021', value: '2021' },
                    { label: '2020', value: '2020' },
                    { label: '2019', value: '2019' },
                    { label: '2018', value: '2018' },
                    { label: '2017', value: '2017' },
                    { label: '2016', value: '2016' },
                  ]}
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                />
                <DcDropDown
                  dropdownSize="sm"
                  options={[
                    { label: 'All Channel', value: 'all' },
                    { label: 'E-Commerce', value: 'e-commerce' },
                    { label: 'Distribution', value: 'distribution' },
                    { label: 'Retail', value: 'retail' },
                  ]}
                  value={selectedChannel}
                  onChange={(e) => setSelectedChannel(e.target.value)}
                />
              </DcFlexBox>
            </DcFlexBox>
          </SectionContent>
        </Section>
        &nbsp;
        <Section style={{ padding: '0px 20px 20px' }}>
          <SectionContent>
            <SalesTable
              data={salesRows}
              onRowClick={handleRowClick}
              isLoading={isLoading}
            />
          </SectionContent>
        </Section>
      </PixelPageContainer>

      <DcOverlaySlider
        isOpen={showUploadSlider}
        onClose={() => setShowUploadSlider(false)}
        title="Upload Sales Data"
        width="40%"
        padding="0px"
      >
        <BulkSalesUpload
          onSalesParsed={() => {
            fetchSalesHistory()
          }}
          onClose={() => setShowUploadSlider(false)}
        />
      </DcOverlaySlider>

      <DcOverlaySlider
        isOpen={showDetailSlider}
        onClose={() => setShowDetailSlider(false)}
        title="Sales Detail"
        width="40%"
        padding="20px"
      >
        {detailLoading ? (
          <PixelSpinner isShow={true} />
        ) : saleDetail && selectedSale ? (
          <>
            <div
              style={{
                marginBottom: '16px',
                padding: '14px 16px',
                borderRadius: '12px',
                border: '1px solid #e5e7eb',
                background:
                  'linear-gradient(135deg, rgba(59,130,246,0.06), rgba(16,185,129,0.04))',
                boxShadow: '0 6px 18px rgba(15,23,42,0.06)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '10px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: '999px',
                      backgroundColor: colors.primary,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <GrowthFactorIcon color="#ffffff" size={20} />
                  </div>
                  <div>
                    <DcText style={{ fontWeight: 600, fontSize: '15px' }}>
                      {selectedSale.sale_channel.toUpperCase()}
                    </DcText>
                    <DcText style={{ fontSize: '12px', color: '#6b7280' }}>
                      {selectedSale.sale_month == 1
                        ? 'January'
                        : selectedSale.sale_month == 2
                        ? 'February'
                        : selectedSale.sale_month == 3
                        ? 'March'
                        : selectedSale.sale_month == 4
                        ? 'April'
                        : selectedSale.sale_month == 5
                        ? 'May'
                        : selectedSale.sale_month == 6
                        ? 'June'
                        : selectedSale.sale_month == 7
                        ? 'July'
                        : selectedSale.sale_month == 8
                        ? 'August'
                        : selectedSale.sale_month == 9
                        ? 'September'
                        : selectedSale.sale_month == 10
                        ? 'October'
                        : selectedSale.sale_month == 11
                        ? 'November'
                        : 'December'}{' '}
                      / {selectedSale.sale_year}
                    </DcText>
                  </div>
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    fontSize: '12px',
                    color: '#6b7280',
                  }}
                >
                  <CalendarDaysIcon color="#6b7280" size={16} />
                  <span>
                    <PixelDate
                      value={selectedSale.created_at.date}
                      format="DD/MM/YYYY"
                      className={''}
                    />
                  </span>
                </div>
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                  gap: 10,
                }}
              >
                <div
                  style={{
                    padding: '8px 10px',
                    borderRadius: '10px',
                    backgroundColor: '#ffffff',
                    border: '1px solid #e5e7eb',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <div
                    style={{
                      width: 26,
                      height: 26,
                      borderRadius: '999px',
                      backgroundColor: 'rgba(59,130,246,0.08)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <TotalProductsIcon color={colors.primary} size={20} />
                  </div>
                  <div>
                    <DcText style={{ fontSize: '11px', color: '#6b7280' }}>
                      Total Products
                    </DcText>
                    <DcText style={{ fontWeight: 600, fontSize: '14px' }}>
                      {selectedSale.total_products}
                    </DcText>
                  </div>
                </div>

                <div
                  style={{
                    padding: '8px 10px',
                    borderRadius: '10px',
                    backgroundColor: '#ffffff',
                    border: '1px solid #e5e7eb',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <div
                    style={{
                      width: 26,
                      height: 26,
                      borderRadius: '999px',
                      backgroundColor: 'rgba(16,185,129,0.08)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <ArrowTrendUpIcon color="#10b981" size={20} />
                  </div>
                  <div>
                    <DcText style={{ fontSize: '11px', color: '#6b7280' }}>
                      Total Qty Sold
                    </DcText>
                    <DcText style={{ fontWeight: 600, fontSize: '14px' }}>
                      {selectedSale.total_qty_sold}
                    </DcText>
                  </div>
                </div>

                <div
                  style={{
                    padding: '8px 10px',
                    borderRadius: '10px',
                    backgroundColor: '#ffffff',
                    border: '1px solid #e5e7eb',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <div
                    style={{
                      width: 26,
                      height: 26,
                      borderRadius: '999px',
                      backgroundColor: 'rgba(249,115,22,0.08)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <ArrowTrendUpIcon color="#f97316" size={20} />
                  </div>
                  <div>
                    <DcText style={{ fontSize: '11px', color: '#6b7280' }}>
                      Growth Factor
                    </DcText>
                    <DcText style={{ fontWeight: 600, fontSize: '14px' }}>
                      {selectedSale.growth_factor}
                    </DcText>
                  </div>
                </div>
              </div>
            </div>

            <SectionContent style={{ padding: '0px' }}>
              <PixelTable
                columns={detailColumns}
                data={saleDetail}
                isSearchable={false}
                fixedHeader
                fixedHeaderScrollHeight="calc(100vh - 260px)"
                pagination={saleDetail.length > 10}
                paginationPerPage={15}
                progressPending={detailLoading}
              />
            </SectionContent>
          </>
        ) : (
          <DcText>No detail available.</DcText>
        )}
      </DcOverlaySlider>
    </>
  )
}

export default Sales
